import React, { useState, useEffect } from "react";
import "../../styles/Global.css";
import styles from "../../pages/Login.module.css";

interface LegacyUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  userData: {
    legacy_id: string;
    email: string;
    full_name: string;
    created_at: string;
    credit_balance: number;
  };
  onMigrationComplete: () => void;
}

/**
 * Modal component for legacy users to reset their password and migrate their account
 * Uses Supabase's password reset flow instead of account creation
 */
const LegacyUserModal: React.FC<LegacyUserModalProps> = ({
  isOpen,
  onClose,
  email,
  userData,
  onMigrationComplete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState<"info" | "reset-sent">("info");

  // Reset component state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep("info");
      setError("");
    }
  }, [isOpen]);

  /**
   * Handle password reset email sending
   */
  const handlePasswordReset = async () => {
    setIsLoading(true);
    setError("");

    try {
      // Use the new backend endpoint for legacy users
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";
      const response = await fetch(
        `${API_URL}/api/legacy-user/create-and-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Legacy user create and reset error:", errorData);
        setError(
          errorData.error ||
            "Failed to send password reset email. Please try again."
        );
        return;
      }

      setStep("reset-sent");
      (window as any).showNotification?.(
        "Password reset email sent! Please check your inbox and click the link.",
        "success"
      );
    } catch (err) {
      console.error("Password reset error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {step === "info" && (
          <>
            <h2 className={styles.modalTitle}>Welcome Back!</h2>
            <p className={styles.modalSubtitle}>
              We found your account from our previous system. To continue using
              your account, please reset your password.
            </p>

            <div className={styles.userInfo}>
              <div className={styles.userInfoItem}>
                <strong>Name:</strong> {userData.full_name}
              </div>
              <div className={styles.userInfoItem}>
                <strong>Email:</strong> {userData.email}
              </div>
              <div className={styles.userInfoItem}>
                <strong>Credits:</strong> {userData.credit_balance}
              </div>
              <div className={styles.userInfoItem}>
                <strong>Member since:</strong>{" "}
                {new Date(userData.created_at).toLocaleDateString()}
              </div>
            </div>

            <div className={styles.migrationSteps}>
              <h3>Migration Process:</h3>
              <ol>
                <li>Click "Reset Password" to receive a reset email</li>
                <li>Check your email and click the reset link</li>
                <li>Set your new password</li>
                <li>Your account and data will be automatically migrated</li>
              </ol>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={handlePasswordReset}
                disabled={isLoading}
                className={styles.submitButton}
              >
                {isLoading ? "Sending Reset Email..." : "Reset Password"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className={styles.cancelButton}
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {step === "reset-sent" && (
          <>
            <div className={styles.successContainer}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.successIcon}
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z"
                  fill="currentColor"
                />
              </svg>

              <h3 className={styles.successHeading}>Reset Email Sent!</h3>

              <p className={styles.successMessage}>
                We've sent a password reset link to <strong>{email}</strong>
              </p>

              <div className={styles.resetInstructions}>
                <h4>Next Steps:</h4>
                <ol>
                  <li>Check your email inbox (and spam folder)</li>
                  <li>Click the "Reset Password" link in the email</li>
                  <li>Set your new password</li>
                  <li>
                    You'll be automatically logged in and your account migrated
                  </li>
                </ol>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={onClose}
                  className={styles.submitButton}
                >
                  Got It
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LegacyUserModal;
